<template>
  <div
    class="feature-container"
    @mouseenter="pauseAutoPlay"
    @mouseleave="resumeAutoPlay"
  >
    <!-- 标题 -->
    <h2 v-if="data.title" class="feature-title">{{ data.title }}</h2>

    <!-- 副标题 -->
    <div v-if="data.subtitle" class="feature-sub">
      <div class="sub-content">
        <p v-if="typeof data.subtitle === 'string'">
          {{ data.subtitle }}
        </p>
        <ul v-else>
          <li v-for="(item, i) in data.subtitle" :key="i">
            {{ item }}
          </li>
        </ul>
      </div>
    </div>

    <!-- 媒体区域 -->
    <div
      class="media-wrapper"
      :style="{ aspectRatio: mediaAspectRatio }"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
    >
      <!-- 轨道 -->
      <div
        class="media-track"
        :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
      >
        <!-- 视频 -->
        <div v-if="hasVideo" class="media-item">
          <video
            ref="videoPlayer"
            class="media-video"
            :poster="currentVideoPoster"
            muted
            playsinline
            webkit-playsinline
            @click="toggleVideo"
            @ended="onVideoEnd"
          >
            <source :src="currentVideoSrc" type="video/mp4" />
          </video>

          <div v-if="currentVideoDesc" class="media-desc">
            {{ currentVideoDesc }}
          </div>

          <button class="video-control" @click.stop="toggleVideo">
            <Icon :fontName="isPlaying ? 'videoPause' : 'videoPlay'" />
          </button>
        </div>

        <!-- 图片 -->
        <div v-for="(img, i) in data.imgs" :key="i" class="media-item">
          <img class="media-image" :src="getImageSrc(img)" :alt="data.title" />
          <div v-if="img.desc" class="media-desc">
            {{ img.desc }}
          </div>
        </div>
      </div>

      <!-- 控制按钮 -->
      <button class="nav-control prev" @click="manualSwitch(-1)">‹</button>
      <button class="nav-control next" @click="manualSwitch(1)">›</button>
    </div>
  </div>
</template>
<script>
import Icon from "@/components/Icon";

export default {
  name: "FeatureMedia",
  components: { Icon },
  props: {
    data: { type: Object, required: true },
  },
  data() {
    return {
      currentIndex: 0,
      isPlaying: false,
      windowWidth: window.innerWidth,

      // 自动轮播
      autoTimer: null,
      autoDelay: 4500,

      // 手势
      touchStartX: 0,
      touchDeltaX: 0,
    };
  },
  computed: {
    basePath() {
      return "/assets/products/";
    },
    hasVideo() {
      return this.data.videos && this.data.videos.length > 0;
    },
    totalMedia() {
      return this.hasVideo ? this.data.imgs.length + 1 : this.data.imgs.length;
    },
    isMobile() {
      return this.windowWidth <= 768;
    },
    mediaAspectRatio() {
      return this.isMobile ? "3/4" : "5/3";
    },
    currentVideoSrc() {
      return this.hasVideo ? this.basePath + this.data.videos[0].src : "";
    },
    currentVideoPoster() {
      if (!this.hasVideo) return "";
      const v = this.data.videos[0];
      return (
        this.basePath +
        (this.isMobile && v.posterMobile ? v.posterMobile : v.poster)
      );
    },
    currentVideoDesc() {
      return this.hasVideo ? this.data.videos[0].desc || "" : "";
    },
  },
  methods: {
    /* ---------- 图片 ---------- */
    getImageSrc(img) {
      return (
        this.basePath +
        (this.isMobile && img.srcMobile ? img.srcMobile : img.src)
      );
    },

    /* ---------- 切换 ---------- */
    switchMedia(step) {
      this.currentIndex =
        (this.currentIndex + step + this.totalMedia) % this.totalMedia;

      const video = this.$refs.videoPlayer;
      if (video) {
        video.pause();
        this.isPlaying = false;
      }
    },
    manualSwitch(step) {
      this.pauseAutoPlay();
      this.switchMedia(step);
      this.resumeAutoPlay();
    },

    /* ---------- 视频 ---------- */
    toggleVideo() {
      const video = this.$refs.videoPlayer;
      if (!video) return;

      if (video.paused) {
        video.play();
        this.isPlaying = true;
        this.pauseAutoPlay();
      } else {
        video.pause();
        this.isPlaying = false;
        this.resumeAutoPlay();
      }
    },
    onVideoEnd() {
      this.isPlaying = false;
      this.resumeAutoPlay();
    },

    /* ---------- 自动轮播 ---------- */
    startAutoPlay() {
      if (this.totalMedia <= 1) return;
      this.autoTimer = setInterval(() => {
        if (!this.isPlaying) {
          this.switchMedia(1);
        }
      }, this.autoDelay);
    },
    pauseAutoPlay() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    },
    resumeAutoPlay() {
      if (!this.autoTimer) {
        this.startAutoPlay();
      }
    },

    /* ---------- 手势 ---------- */
    onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchDeltaX = 0;
      this.pauseAutoPlay();
    },
    onTouchMove(e) {
      this.touchDeltaX = e.touches[0].clientX - this.touchStartX;
    },
    onTouchEnd() {
      if (Math.abs(this.touchDeltaX) > 50) {
        this.switchMedia(this.touchDeltaX > 0 ? -1 : 1);
      }
      this.resumeAutoPlay();
    },
  },
  mounted() {
    window.addEventListener("resize", () => {
      this.windowWidth = window.innerWidth;
    });
    this.startAutoPlay();
  },
  beforeDestroy() {
    this.pauseAutoPlay();
  },
};
</script>
<style lang="less" scoped>
@import "~@/assets/styles/variables.less";
@import "~@/assets/styles/mixins.less";

.feature-container {
  margin: 1.5rem auto;
}

.feature-title {
  font: 700 28px/1.2 @font-family-title;
  color: @color-text-title;
  margin-bottom: 0.5rem;
}

.feature-sub {
  margin-bottom: 1.5rem;

  .sub-content {
    background: rgba(0, 0, 0, 0.45);
    border-radius: 12px;
    padding: 1rem 1.2rem;
    backdrop-filter: blur(4px);
    color: #fff;
  }

  p,
  li {
    font-size: 16px;
    line-height: 1.6;
    font-weight: 500;
    margin: 0;
  }

  ul {
    list-style: none;
    padding: 0;
    margin-top: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}

/* ---------- 媒体 ---------- */

.media-wrapper {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
}

.media-track {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.media-item {
  flex: 0 0 100%;
  position: relative;
}

.media-image,
.media-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.media-desc {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.75rem 1rem;
  font: 500 14px/1.4 @font-family-base;
  color: #fff;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: 5;
}

/* ---------- 视频按钮 ---------- */

.video-control {
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s;
}

/* ---------- 导航 ---------- */

.nav-control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  color: @color-primary;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.85);
    transform: translateY(-50%) scale(1.1);
  }

  &.prev {
    left: 1.5rem;
  }
  &.next {
    right: 1.5rem;
  }
}
</style>
